import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import {
  Download, Upload, RefreshCw, HardDrive, AlertTriangle, Loader2,
  Trash2, Lock, ChevronDown, ChevronRight, Users, KeyRound
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface BackupInfo {
  filename: string;
  sizeBytes: number;
  created: string;
  type: 'local' | 'imported';
  manifest: {
    type?: string;
    created?: string;
    scrambled?: boolean;
    stats?: { totalFiles?: number; totalSize?: number; userCount?: number; users?: string[] };
    selections?: { platform?: boolean; userIds?: string[] };
    userDirectory?: Record<string, { displayName?: string; email?: string; role?: string }>;
    version?: string;
  } | null;
}

interface CategoryUser {
  userId: string;
  email: string;
  displayName: string;
  role: string;
}

interface RestorePreview {
  manifest: {
    type: string;
    created: string;
    scrambled: boolean;
    stats: { totalFiles?: number; totalSize?: number; userCount?: number };
  };
  hasPlatform: boolean;
  users: Array<{
    userId: string;
    dirName: string;
    displayName: string;
    email: string;
    role: string;
  }>;
  isImported: boolean;
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export const BackupManagement: React.FC = () => {
  // Core state
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [selectedBackup, setSelectedBackup] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [operationOutput, setOperationOutput] = useState('');

  // Create Backup state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState<'full' | 'selective'>('full');
  const [createName, setCreateName] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [usersExpanded, setUsersExpanded] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<CategoryUser[]>([]);
  const [platformFileCount, setPlatformFileCount] = useState(0);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Export (Download) state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPassword, setExportPassword] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  // Import state
  const [isImporting, setIsImporting] = useState(false);

  // Restore state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [restorePassword, setRestorePassword] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restorePreview, setRestorePreview] = useState<RestorePreview | null>(null);
  const [restorePlatform, setRestorePlatform] = useState(false);
  const [restoreUserIds, setRestoreUserIds] = useState<Set<string>>(new Set());
  const [restoreUsersExpanded, setRestoreUsersExpanded] = useState(true);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Misc

  const isBusy = isCreating || isImporting || isRestoring || isDeleting || isExporting;

  // ─────────────────────────────────────────────────────────────
  // Auth helpers
  // ─────────────────────────────────────────────────────────────

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const getAuthHeadersRaw = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  // ─────────────────────────────────────────────────────────────
  // Format helpers
  // ─────────────────────────────────────────────────────────────

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso: string) => new Date(iso).toLocaleString();

  // ─────────────────────────────────────────────────────────────
  // Load backups
  // ─────────────────────────────────────────────────────────────

  const loadBackups = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await fetch('/api/admin/backup/list', { headers: getAuthHeaders() });
      const result = await response.json();
      if (result.success) {
        setBackups(result.backups);
        setSelectedBackup(prev => {
          if (!prev || !result.backups.some((b: BackupInfo) => b.filename === prev)) {
            return result.backups.length > 0 ? result.backups[0].filename : '';
          }
          return prev;
        });
      } else {
        setError(result.error || 'Failed to load backups');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { loadBackups(); }, []);

  // ─────────────────────────────────────────────────────────────
  // CREATE BACKUP
  // ─────────────────────────────────────────────────────────────

  const openCreateWizard = async () => {
    setShowCreateModal(true);
    setCreateMode('full');
    setCreateName('');
    setSelectedPlatform(false);
    setSelectedUserIds(new Set());
    setUsersExpanded(false);

    try {
      setIsLoadingCategories(true);
      const response = await fetch('/api/admin/backup/categories', { headers: getAuthHeaders() });
      const result = await response.json();
      if (result.success) {
        setPlatformFileCount(result.platformFileCount || 0);
        setAvailableUsers(result.users || []);
      }
    } catch {
      // silent — modal will show empty state
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const allUsersSelected = availableUsers.length > 0 && selectedUserIds.size === availableUsers.length;
  const someUsersSelected = selectedUserIds.size > 0 && selectedUserIds.size < availableUsers.length;

  const toggleAllUsers = () => {
    if (allUsersSelected) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(availableUsers.map(u => u.userId)));
    }
  };

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const canCreate = createMode === 'full' || selectedPlatform || selectedUserIds.size > 0;
  const nameValid = !createName || /^[a-zA-Z0-9._-]{1,50}$/.test(createName);

  const handleCreate = async () => {
    if (!canCreate || !nameValid) return;
    setError('');
    setIsCreating(true);
    setOperationOutput('');

    try {
      const body: Record<string, unknown> = { mode: createMode };
      if (createName) body.name = createName;
      if (createMode === 'selective') {
        body.platform = selectedPlatform;
        body.userIds = Array.from(selectedUserIds);
      }

      const response = await fetch('/api/admin/backup/create', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (result.success) {
        setOperationOutput(result.output || `Backup created: ${result.filename}`);
        setShowCreateModal(false);
        await loadBackups();
      } else {
        setError(result.error || 'Backup failed');
        if (result.output) setOperationOutput(result.output);
      }
    } catch {
      setError('Backup operation failed');
    } finally {
      setIsCreating(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // EXPORT (Download as .mcexport)
  // ─────────────────────────────────────────────────────────────

  const openExportModal = () => {
    if (!selectedBackup || !selectedBackup.endsWith('.zip')) return;
    setExportPassword('');
    setShowExportModal(true);
  };

  const handleExport = async () => {
    if (!selectedBackup || exportPassword.length < 8) return;
    setIsExporting(true);
    setError('');

    try {
      const response = await fetch('/api/admin/backup/export', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ filename: selectedBackup, password: exportPassword })
      });

      if (!response.ok) {
        const result = await response.json();
        setError(result.error || 'Export failed');
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = selectedBackup.replace('.zip', '.mcexport');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setShowExportModal(false);
      setOperationOutput(`Exported: ${selectedBackup.replace('.zip', '.mcexport')}`);
    } catch {
      setError('Failed to export backup');
    } finally {
      setIsExporting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // IMPORT (.mcexport upload)
  // ─────────────────────────────────────────────────────────────

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      setError('');
      setOperationOutput('');

      const formData = new FormData();
      formData.append('backup', file);

      const response = await fetch('/api/admin/backup/import', {
        method: 'POST',
        headers: getAuthHeadersRaw(),
        body: formData
      });
      const result = await response.json();
      if (result.success) {
        setOperationOutput(`Imported: ${result.filename} (${formatSize(result.sizeBytes)})`);
        await loadBackups();
      } else {
        setError(result.error || 'Import failed');
      }
    } catch {
      setError('Failed to import backup');
    } finally {
      setIsImporting(false);
      e.target.value = '';
    }
  };

  // ─────────────────────────────────────────────────────────────
  // RESTORE
  // ─────────────────────────────────────────────────────────────

  const selectedBackupInfo = backups.find(b => b.filename === selectedBackup);
  const isImportedBackup = selectedBackupInfo?.type === 'imported';

  const initiateRestore = () => {
    if (!selectedBackup) return;

    if (isImportedBackup) {
      // .mcexport — need password first
      setRestorePassword('');
      setShowPasswordModal(true);
    } else {
      // .zip — go straight to preview
      loadRestorePreview(selectedBackup, undefined);
    }
  };

  const handlePasswordSubmit = () => {
    if (!restorePassword) return;
    setShowPasswordModal(false);
    loadRestorePreview(selectedBackup, restorePassword);
  };

  const loadRestorePreview = async (filename: string, password: string | undefined) => {
    setIsLoadingPreview(true);
    setError('');

    try {
      const body: Record<string, unknown> = { filename };
      if (password) body.password = password;

      const response = await fetch('/api/admin/backup/restore/preview', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (result.success) {
        setRestorePreview(result as RestorePreview);
        // Default: select everything
        setRestorePlatform(result.hasPlatform);
        setRestoreUserIds(new Set(result.users.map((u: { userId: string }) => u.userId)));
        setRestoreUsersExpanded(true);
        setShowRestoreModal(true);
      } else {
        setError(result.error || 'Failed to preview backup');
      }
    } catch {
      setError('Failed to preview backup');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const allRestoreUsersSelected = restorePreview
    ? restorePreview.users.length > 0 && restoreUserIds.size === restorePreview.users.length
    : false;
  const someRestoreUsersSelected = restorePreview
    ? restoreUserIds.size > 0 && restoreUserIds.size < restorePreview.users.length
    : false;

  const toggleAllRestoreUsers = () => {
    if (!restorePreview) return;
    if (allRestoreUsersSelected) {
      setRestoreUserIds(new Set());
    } else {
      setRestoreUserIds(new Set(restorePreview.users.map(u => u.userId)));
    }
  };

  const toggleRestoreUser = (userId: string) => {
    setRestoreUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const canRestore = restorePlatform || restoreUserIds.size > 0;

  const handleRestore = async () => {
    if (!selectedBackup || !canRestore) return;
    setShowRestoreModal(false);
    setIsRestoring(true);
    setError('');
    setOperationOutput('');

    try {
      const body: Record<string, unknown> = {
        filename: selectedBackup,
        restorePlatform,
        restoreUserIds: Array.from(restoreUserIds)
      };
      if (isImportedBackup && restorePassword) {
        body.password = restorePassword;
      }

      const response = await fetch('/api/admin/backup/restore', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      const result = await response.json();
      if (result.success) {
        setOperationOutput(result.output || 'Restore completed successfully');
        await loadBackups();
      } else {
        setError(result.error || 'Restore failed');
        if (result.output) setOperationOutput(result.output);
      }
    } catch {
      setError('Failed to restore backup');
    } finally {
      setIsRestoring(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // DELETE
  // ─────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    setShowDeleteModal(false);
    try {
      setIsDeleting(true);
      setError('');
      const response = await fetch(`/api/admin/backup/delete/${encodeURIComponent(selectedBackup)}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      const result = await response.json();
      if (result.success) {
        setOperationOutput(`Deleted: ${selectedBackup}`);
        await loadBackups();
      } else {
        setError(result.error || 'Delete failed');
      }
    } catch {
      setError('Failed to delete backup');
    } finally {
      setIsDeleting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // DOWNLOAD (direct .zip or .mcexport)
  // ─────────────────────────────────────────────────────────────


  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────

  return (
    <div className="backup-management">
      <div className="backup-header">
        <h1><HardDrive size={24} /> Backup Management</h1>
        <p>Create, export, import, and restore system backups</p>
      </div>

      {error && (
        <div className="backup-error">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* ══════════ Action Bar ══════════ */}
      <div className="backup-section">
        <div className="action-bar">
          <button className="btn btn-primary" onClick={openCreateWizard} disabled={isBusy}>
            <HardDrive size={16} /> Create Backup
          </button>
          <label className={`btn btn-secondary upload-label ${isBusy ? 'disabled' : ''}`}>
            {isImporting ? (
              <><Loader2 size={16} className="spin" /> Importing...</>
            ) : (
              <><Upload size={16} /> Import .mcexport</>
            )}
            <input
              type="file"
              accept=".mcexport"
              onChange={handleImport}
              disabled={isBusy}
              style={{ display: 'none' }}
            />
          </label>
        </div>
      </div>

      {/* ══════════ Available Backups ══════════ */}
      <div className="backup-section">
        <h2>
          Available Backups
          <button
            className="btn btn-ghost btn-sm"
            onClick={loadBackups}
            disabled={isLoading || isBusy}
            title="Refresh list"
          >
            <RefreshCw size={14} className={isLoading ? 'spin' : ''} />
          </button>
        </h2>

        {backups.length === 0 ? (
          <p className="backup-empty">No backups found.</p>
        ) : (
          <>
            <select
              className="backup-select"
              value={selectedBackup}
              onChange={(e) => setSelectedBackup(e.target.value)}
              disabled={isBusy}
            >
              {backups.map((b) => (
                <option key={b.filename} value={b.filename}>
                  {b.type === 'imported' ? '(imported) ' : ''}{b.filename} ({formatSize(b.sizeBytes)}) - {formatDate(b.created)}
                </option>
              ))}
            </select>

            {/* Manifest info for local backups */}
            {selectedBackupInfo?.manifest && (
              <div className="backup-manifest">
                {selectedBackupInfo.manifest.type && (
                  <span className="manifest-badge">{selectedBackupInfo.manifest.type}</span>
                )}
                {selectedBackupInfo.manifest.stats?.totalFiles !== undefined && (
                  <span>{selectedBackupInfo.manifest.stats.totalFiles} files</span>
                )}
                {selectedBackupInfo.manifest.stats?.userCount !== undefined && (
                  <span>{selectedBackupInfo.manifest.stats.userCount} users</span>
                )}
                {selectedBackupInfo.manifest.scrambled && (
                  <span className="badge-scrambled">Scrambled</span>
                )}
              </div>
            )}

            {/* Imported badge */}
            {isImportedBackup && (
              <div className="imported-badge-row">
                <KeyRound size={14} />
                <span>Imported backup — password required to restore</span>
              </div>
            )}

            <div className="backup-actions">
              {/* Export: only for local .zip backups */}
              {selectedBackupInfo?.type === 'local' && (
                <button
                  className="btn btn-secondary"
                  onClick={openExportModal}
                  disabled={!selectedBackup || isBusy}
                  title="Export as password-protected .mcexport for transfer"
                >
                  {isExporting ? (
                    <><Loader2 size={16} className="spin" /> Exporting...</>
                  ) : (
                    <><Download size={16} /> Export</>
                  )}
                </button>
              )}

              {/* Restore */}
              <button
                className={`btn ${isImportedBackup ? 'btn-warning' : 'btn-danger'}`}
                onClick={initiateRestore}
                disabled={!selectedBackup || isBusy || isLoadingPreview}
                title={isImportedBackup ? 'Restore (password required)' : 'Restore backup'}
              >
                {isRestoring || isLoadingPreview ? (
                  <><Loader2 size={16} className="spin" /> {isLoadingPreview ? 'Loading...' : 'Restoring...'}</>
                ) : (
                  <>{isImportedBackup ? <KeyRound size={16} /> : <RefreshCw size={16} />} Restore</>
                )}
              </button>

              {/* Delete */}
              <button
                className="btn btn-danger-outline"
                onClick={() => setShowDeleteModal(true)}
                disabled={!selectedBackup || isBusy}
              >
                {isDeleting ? (
                  <><Loader2 size={16} className="spin" /> Deleting...</>
                ) : (
                  <><Trash2 size={16} /> Delete</>
                )}
              </button>
            </div>
          </>
        )}
      </div>

      {/* ══════════ Operation Output ══════════ */}
      {operationOutput && (
        <div className="backup-section">
          <h2>Operation Output</h2>
          <pre className="backup-output">{operationOutput}</pre>
        </div>
      )}

      {/* ══════════════════════════════════════ */}
      {/*                MODALS                  */}
      {/* ══════════════════════════════════════ */}

      {/* ── Create Backup Modal ── */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => !isCreating && setShowCreateModal(false)}
        title="Create Backup"
        size="medium"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)} disabled={isCreating}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCreate}
              disabled={isCreating || !canCreate || !nameValid}
            >
              {isCreating ? (
                <><Loader2 size={16} className="spin" /> Creating...</>
              ) : (
                <><HardDrive size={16} /> Create Backup</>
              )}
            </button>
          </>
        }
      >
        {isLoadingCategories ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Loader2 size={24} className="spin" />
            <p style={{ marginTop: 8, color: 'var(--text-secondary)' }}>Loading...</p>
          </div>
        ) : (
          <div className="create-wizard-body">
            {/* Mode Selection */}
            <div className="mode-selection">
              <label className="mode-option">
                <input
                  type="radio"
                  name="backupMode"
                  checked={createMode === 'full'}
                  onChange={() => setCreateMode('full')}
                  disabled={isCreating}
                />
                <div>
                  <strong>Full Backup</strong>
                  <span className="mode-desc">Everything — platform config, all users, audit logs</span>
                </div>
              </label>
              <label className="mode-option">
                <input
                  type="radio"
                  name="backupMode"
                  checked={createMode === 'selective'}
                  onChange={() => setCreateMode('selective')}
                  disabled={isCreating}
                />
                <div>
                  <strong>Selective Backup</strong>
                  <span className="mode-desc">Choose platform config and/or specific users</span>
                </div>
              </label>
            </div>

            {/* Selective Options */}
            {createMode === 'selective' && (
              <div className="selective-options">
                {/* Platform */}
                <label className="tree-item tree-root">
                  <input
                    type="checkbox"
                    checked={selectedPlatform}
                    onChange={() => setSelectedPlatform(!selectedPlatform)}
                    disabled={isCreating}
                  />
                  <HardDrive size={16} />
                  <span className="tree-label">Platform Config</span>
                  <span className="tree-meta">{platformFileCount} files</span>
                </label>

                <div className="tree-divider" />

                {/* Users */}
                <div className="tree-item tree-root">
                  <button className="tree-toggle" onClick={() => setUsersExpanded(!usersExpanded)}>
                    {usersExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <label className="tree-item-inner">
                    <input
                      type="checkbox"
                      checked={allUsersSelected}
                      ref={el => { if (el) el.indeterminate = someUsersSelected; }}
                      onChange={toggleAllUsers}
                      disabled={isCreating || availableUsers.length === 0}
                    />
                    <Users size={16} />
                    <span className="tree-label">Users</span>
                    <span className="tree-meta">
                      {selectedUserIds.size > 0
                        ? `${selectedUserIds.size} of ${availableUsers.length} selected`
                        : `${availableUsers.length} available`}
                    </span>
                  </label>
                </div>

                {usersExpanded && (
                  <div className="tree-children">
                    {availableUsers.map(u => (
                      <label key={u.userId} className="tree-item tree-child">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.has(u.userId)}
                          onChange={() => toggleUser(u.userId)}
                          disabled={isCreating}
                        />
                        <span className="tree-user-name">{u.displayName || u.email}</span>
                        <span className="tree-user-role">{u.role}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Optional Name */}
            <div className="name-section">
              <label className="name-label">
                Backup Name <span className="name-hint">(optional)</span>
              </label>
              <input
                type="text"
                className={`wizard-input ${createName && !nameValid ? 'input-error' : ''}`}
                placeholder="e.g. pre-migration, court-data"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                disabled={isCreating}
                maxLength={50}
              />
              {createName && !nameValid && (
                <span className="input-error-text">Letters, numbers, periods, dashes, underscores only</span>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ── Export Password Modal ── */}
      <Modal
        isOpen={showExportModal}
        onClose={() => !isExporting && setShowExportModal(false)}
        title="Export Backup"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowExportModal(false)} disabled={isExporting}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleExport}
              disabled={isExporting || exportPassword.length < 8}
            >
              {isExporting ? (
                <><Loader2 size={16} className="spin" /> Encrypting...</>
              ) : (
                <><Download size={16} /> Export &amp; Download</>
              )}
            </button>
          </>
        }
      >
        <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 14 }}>
          The backup will be encrypted with this password and downloaded as a <strong>.mcexport</strong> file
          for transfer to another environment.
        </p>
        <div className="password-field">
          <Lock size={14} />
          <input
            type="password"
            className="wizard-input"
            placeholder="Minimum 8 characters"
            value={exportPassword}
            onChange={(e) => setExportPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && exportPassword.length >= 8 && !isExporting) handleExport(); }}
            disabled={isExporting}
            autoFocus
          />
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 8 }}>
          File: {selectedBackup}
        </p>
      </Modal>

      {/* ── Restore Password Modal (for .mcexport) ── */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title="Enter Backup Password"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowPasswordModal(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePasswordSubmit}
              disabled={!restorePassword}
            >
              <KeyRound size={16} /> Continue
            </button>
          </>
        }
      >
        <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: 14 }}>
          This is an imported backup. Enter the password used during export to decrypt it.
        </p>
        <div className="password-field">
          <Lock size={14} />
          <input
            type="password"
            className="wizard-input"
            placeholder="Export password"
            value={restorePassword}
            onChange={(e) => setRestorePassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && restorePassword) handlePasswordSubmit(); }}
            autoFocus
          />
        </div>
      </Modal>

      {/* ── Restore Selection Modal ── */}
      <Modal
        isOpen={showRestoreModal}
        onClose={() => !isRestoring && setShowRestoreModal(false)}
        title="Restore Backup"
        variant="warning"
        size="medium"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowRestoreModal(false)} disabled={isRestoring}>
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleRestore}
              disabled={isRestoring || !canRestore}
            >
              {isRestoring ? (
                <><Loader2 size={16} className="spin" /> Restoring...</>
              ) : (
                <><RefreshCw size={16} /> Restore Selected</>
              )}
            </button>
          </>
        }
      >
        {restorePreview && (
          <div className="restore-wizard-body">
            <div className="restore-info">
              <AlertTriangle size={16} style={{ color: 'var(--accent-orange)', flexShrink: 0 }} />
              <span>A safety snapshot will be created automatically before restoring. Select what to restore:</span>
            </div>

            <div className="restore-meta">
              <span>Created: {formatDate(restorePreview.manifest.created)}</span>
              {restorePreview.manifest.stats?.totalFiles !== undefined && (
                <span>{restorePreview.manifest.stats.totalFiles} files</span>
              )}
              {restorePreview.isImported && <span className="badge-imported">Imported</span>}
            </div>

            {/* Platform checkbox */}
            {restorePreview.hasPlatform && (
              <label className="tree-item tree-root">
                <input
                  type="checkbox"
                  checked={restorePlatform}
                  onChange={() => setRestorePlatform(!restorePlatform)}
                />
                <HardDrive size={16} />
                <span className="tree-label">Platform Config</span>
              </label>
            )}

            {restorePreview.hasPlatform && restorePreview.users.length > 0 && (
              <div className="tree-divider" />
            )}

            {/* Users */}
            {restorePreview.users.length > 0 && (
              <>
                <div className="tree-item tree-root">
                  <button className="tree-toggle" onClick={() => setRestoreUsersExpanded(!restoreUsersExpanded)}>
                    {restoreUsersExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <label className="tree-item-inner">
                    <input
                      type="checkbox"
                      checked={allRestoreUsersSelected}
                      ref={el => { if (el) el.indeterminate = someRestoreUsersSelected; }}
                      onChange={toggleAllRestoreUsers}
                    />
                    <Users size={16} />
                    <span className="tree-label">Users</span>
                    <span className="tree-meta">
                      {restoreUserIds.size > 0
                        ? `${restoreUserIds.size} of ${restorePreview.users.length} selected`
                        : `${restorePreview.users.length} in backup`}
                    </span>
                  </label>
                </div>

                {restoreUsersExpanded && (
                  <div className="tree-children">
                    {restorePreview.users.map(u => (
                      <label key={u.userId} className="tree-item tree-child">
                        <input
                          type="checkbox"
                          checked={restoreUserIds.has(u.userId)}
                          onChange={() => toggleRestoreUser(u.userId)}
                        />
                        <span className="tree-user-name">{u.displayName || u.email}</span>
                        <span className="tree-user-role">{u.role || ''}</span>
                      </label>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Backup"
        variant="danger"
        size="small"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </>
        }
      >
        <p>
          <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Permanently delete this backup?
        </p>
        <p><strong>{selectedBackup}</strong></p>
        <p>This cannot be undone.</p>
      </Modal>

      <style>{`
        .backup-management {
          padding: 24px;
          max-width: 800px;
        }
        .backup-header h1 {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-primary);
          margin-bottom: 4px;
        }
        .backup-header p {
          color: var(--text-secondary);
          margin-bottom: 24px;
        }
        .backup-error {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: rgba(255, 59, 48, 0.1);
          color: var(--accent-red);
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 14px;
        }
        .backup-section {
          background: var(--bg-tertiary);
          border: 1px solid var(--border-secondary);
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 16px;
        }
        .backup-section h2 {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 16px;
          color: var(--text-primary);
          margin-bottom: 8px;
        }
        .action-bar {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .backup-select {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          margin-bottom: 12px;
        }
        .backup-manifest {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 12px;
          padding: 8px 12px;
          background: var(--bg-secondary);
          border-radius: 6px;
        }
        .manifest-badge {
          background: var(--bg-tertiary);
          padding: 1px 8px;
          border-radius: 4px;
          font-weight: 500;
          text-transform: capitalize;
        }
        .badge-scrambled {
          background: rgba(255, 149, 0, 0.15);
          color: var(--accent-orange);
          padding: 1px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .imported-badge-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--accent-orange);
          margin-bottom: 12px;
          padding: 6px 12px;
          background: rgba(255, 149, 0, 0.08);
          border-radius: 6px;
        }
        .badge-imported {
          background: rgba(255, 149, 0, 0.15);
          color: var(--accent-orange);
          padding: 1px 8px;
          border-radius: 4px;
          font-weight: 500;
          font-size: 12px;
        }
        .backup-actions {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .backup-output {
          background: var(--bg-secondary);
          color: var(--text-primary);
          padding: 16px;
          border-radius: 6px;
          font-size: 13px;
          line-height: 1.5;
          max-height: 400px;
          overflow-y: auto;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .backup-empty {
          color: var(--text-tertiary);
          font-style: italic;
        }
        .upload-label {
          display: inline-flex;
          cursor: pointer;
        }
        .upload-label.disabled {
          pointer-events: none;
          opacity: 0.6;
        }

        /* Buttons */
        .btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
        }
        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-primary {
          background: var(--accent-blue);
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background: var(--accent-blue-hover);
        }
        .btn-secondary {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border-secondary);
        }
        .btn-secondary:hover:not(:disabled) {
          background: var(--bg-selected);
        }
        .btn-danger {
          background: var(--accent-red);
          color: white;
        }
        .btn-danger:hover:not(:disabled) {
          opacity: 0.85;
        }
        .btn-warning {
          background: var(--accent-orange);
          color: white;
        }
        .btn-warning:hover:not(:disabled) {
          opacity: 0.85;
        }
        .btn-danger-outline {
          background: transparent;
          color: var(--accent-red);
          border: 1px solid var(--accent-red);
        }
        .btn-danger-outline:hover:not(:disabled) {
          background: rgba(255, 59, 48, 0.1);
        }
        .btn-ghost {
          background: transparent;
          padding: 4px 8px;
          color: var(--text-secondary);
        }
        .btn-ghost:hover:not(:disabled) {
          background: var(--bg-secondary);
        }
        .btn-sm {
          padding: 4px 8px;
          font-size: 12px;
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Create Wizard */
        .create-wizard-body {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .mode-selection {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
        }
        .mode-option {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 14px;
          border: 1px solid var(--border-secondary);
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.1s;
        }
        .mode-option:hover {
          background: var(--bg-selected);
        }
        .mode-option input[type="radio"] {
          margin-top: 3px;
          accent-color: var(--accent-blue);
        }
        .mode-option strong {
          display: block;
          font-size: 14px;
          color: var(--text-primary);
        }
        .mode-desc {
          display: block;
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 2px;
        }
        .selective-options {
          margin-bottom: 12px;
        }
        .name-section {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid var(--border-secondary);
        }
        .name-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
          margin-bottom: 6px;
        }
        .name-hint {
          font-weight: 400;
          color: var(--text-tertiary);
        }
        .input-error {
          border-color: var(--accent-red) !important;
        }
        .input-error-text {
          display: block;
          font-size: 12px;
          color: var(--accent-red);
          margin-top: 4px;
        }

        /* Tree */
        .tree-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 10px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          color: var(--text-primary);
        }
        .tree-item:hover {
          background: var(--bg-selected);
        }
        .tree-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: var(--accent-blue);
          cursor: pointer;
          flex-shrink: 0;
        }
        .tree-root {
          font-weight: 500;
        }
        .tree-item-inner {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
          cursor: pointer;
        }
        .tree-toggle {
          background: none;
          border: none;
          padding: 2px;
          cursor: pointer;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        .tree-toggle:hover {
          color: var(--text-primary);
        }
        .tree-label {
          flex: 1;
        }
        .tree-meta {
          font-size: 12px;
          color: var(--text-tertiary);
          font-weight: 400;
        }
        .tree-divider {
          height: 1px;
          background: var(--border-secondary);
          margin: 6px 0;
        }
        .tree-children {
          padding-left: 28px;
          display: flex;
          flex-direction: column;
          gap: 1px;
          margin-bottom: 4px;
        }
        .tree-child {
          font-weight: 400;
          padding: 5px 10px;
        }
        .tree-user-name {
          flex: 1;
        }
        .tree-user-role {
          font-size: 11px;
          padding: 1px 8px;
          background: var(--bg-tertiary);
          border-radius: 3px;
          color: var(--text-secondary);
          font-weight: 400;
        }

        /* Password field */
        .password-field {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .password-field .wizard-input {
          flex: 1;
        }

        /* Wizard input */
        .wizard-input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 14px;
          box-sizing: border-box;
        }
        .wizard-input:focus {
          outline: none;
          border-color: var(--accent-blue);
        }

        /* Restore wizard */
        .restore-wizard-body {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .restore-info {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 14px;
          color: var(--text-secondary);
          margin-bottom: 12px;
          padding: 10px 12px;
          background: rgba(255, 149, 0, 0.08);
          border-radius: 6px;
        }
        .restore-meta {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 12px;
          padding: 6px 12px;
          background: var(--bg-secondary);
          border-radius: 6px;
        }
      `}</style>
    </div>
  );
};
